import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, ListChecks, Image as ImageIcon, Workflow, LogIn } from 'lucide-react';
import { useAuth, passwordLogin } from '@/hooks/use-auth';
import { api } from '@/lib/api';

export default function LoginPage() {
  const { user, loading, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  async function handleMagic(e: React.FormEvent) {
    e.preventDefault();
    if (!magicEmail.trim()) return;
    setMagicBusy(true);
    try {
      await api.post('/auth/magic/request', { email: magicEmail.trim().toLowerCase() });
      setMagicSent(true);
    } catch {
      // Same neutral outcome either way: the endpoint never reveals accounts.
      setMagicSent(true);
    } finally {
      setMagicBusy(false);
    }
  }

  async function handlePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setPwBusy(true);
    try {
      await passwordLogin(email.trim().toLowerCase(), password);
      await refresh();
      toast.success('Signed in');
    } catch (err: unknown) {
      // Backend returns 401 with the same message for all failure modes
      toast.error('Invalid email or password');
    } finally {
      setPwBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted text-sm">
        Loading...
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  async function handleGoogle() {
    setBusy(true);
    try {
      const r = await api.get<{ redirect_url: string }>('/auth/google/start');
      window.location.href = r.data.redirect_url;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign in failed';
      toast.error(msg);
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background ambience */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-accent-600/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent-400/10 blur-[120px]" />
      </div>

      {/* Top bar */}
      <header className="px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/kaf-logo.png" alt="KAF PRO" className="h-9 w-9 rounded-full" />
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide text-muted-strong">KAF PRO</div>
            <div className="text-[10px] text-muted uppercase tracking-widest">Media Production</div>
          </div>
        </div>
        <div className="text-[10px] text-muted uppercase tracking-widest">Internal Tool</div>
      </header>

      {/* Hero */}
      <main className="px-6 md:px-10 pt-8 md:pt-16 pb-16 max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        {/* Left: pitch */}
        <section className="space-y-6">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-accent-400 bg-accent-600/10 border border-accent-400/30 rounded-full px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" />
            Production ops &amp; creative warehouse
          </div>

          <h1 className="text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            <span className="text-accent-400">KAF</span>
            <span className="text-muted-strong"> Warehouse</span>
          </h1>

          <p className="text-xl md:text-2xl text-muted-strong leading-snug max-w-lg">
            Where every campaign lives - from <span className="text-accent-400">strategy</span> to <span className="text-accent-400">posted</span>, all in one place.
          </p>

          <p className="text-sm text-muted max-w-lg">
            KAF Warehouse is KAF PRO's internal production-ops platform and creative library. The Marketing
            Strategist drops the plan; Content Creators brief and schedule; Videographers, Designers and Editors
            execute; Art Director and Content Creator sign off; the client sees the final reel in their own feed.
            No more lost WhatsApp messages, no more "who's working on what" - and every approved creative archived
            by client, doctor, specialty, channel, and funnel stage, ready to recall in one click.
          </p>

          {/* Feature row */}
          <div className="grid grid-cols-2 gap-3 max-w-lg pt-2">
            <Feature icon={<Workflow size={16} />} title="6-stage workflow" body="Strategy -> Content -> Production -> Dual approval -> Done -> Posted" />
            <Feature icon={<Calendar size={16} />} title="Two calendars" body="Execution + Posting, owned by Content Creator" />
            <Feature icon={<ListChecks size={16} />} title="Auto-routing" body="Tasks land on the right designation by least-loaded" />
            <Feature icon={<ImageIcon size={16} />} title="Creatives Library" body="Instagram-feed view, shared with the client when approved" />
          </div>
        </section>

        {/* Right: sign-in card */}
        <section className="md:justify-self-end w-full max-w-sm md:ml-auto">
          <div className="bg-bg-card/85 backdrop-blur-xl border border-slate-800 rounded-2xl p-6 md:p-7 shadow-card">
            <div className="text-center space-y-1 mb-6">
              <h2 className="text-lg font-semibold text-muted-strong">Sign in to continue</h2>
              <p className="text-xs text-muted">KAF PRO staff &middot; Google account</p>
            </div>

            <form onSubmit={handlePassword} className="space-y-2.5 mb-4">
              <input
                type="email"
                placeholder="you@kaf4media.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full h-11 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full h-11 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
              />
              <button
                type="submit"
                disabled={pwBusy || !email || !password}
                className="w-full h-11 rounded-lg bg-accent-600 hover:bg-accent-500 text-white font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <LogIn size={14} /> {pwBusy ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-bg-card px-2 text-[10px] uppercase tracking-widest text-muted">or</span>
              </div>
            </div>

            <button
              onClick={handleGoogle}
              disabled={busy}
              className="w-full h-12 rounded-xl bg-white text-slate-900 font-medium text-sm flex items-center justify-center gap-3 hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
            >
              <GoogleIcon />
              {busy ? 'Redirecting...' : 'Continue with Google'}
            </button>

            <div className="mt-5 pt-5 border-t border-slate-800 space-y-2.5">
              <p className="text-[11px] text-muted text-center">
                Client or freelancer? Get a sign-in link by email.
              </p>
              {magicSent ? (
                <p className="text-[11px] text-accent-400 text-center leading-relaxed">
                  If that email is registered, a sign-in link is on its way. It works once and
                  expires in 15 minutes.
                </p>
              ) : (
                <form onSubmit={handleMagic} className="flex gap-2">
                  <input
                    type="email"
                    placeholder="you@yourclinic.com"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    autoComplete="email"
                    className="flex-1 h-10 px-3 rounded-lg border border-line bg-bg text-xs text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
                  />
                  <button
                    type="submit"
                    disabled={magicBusy || !magicEmail.trim()}
                    className="h-10 px-3.5 rounded-lg border border-accent-400/40 text-accent-400 hover:bg-accent-600/10 text-xs font-medium disabled:opacity-50 transition"
                  >
                    {magicBusy ? 'Sending...' : 'Email link'}
                  </button>
                </form>
              )}
            </div>
          </div>

          <p className="text-[10px] text-muted text-center mt-4 tracking-wide">
            Built for KAF PRO Media Production &middot; Dubai
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-6 md:px-10 py-5 border-t border-slate-900/60 text-center text-[10px] text-muted uppercase tracking-widest">
        kaf4media.com &middot; KAF PRO &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="mt-0.5 p-1.5 rounded-lg bg-accent-600/15 text-accent-400 shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-muted-strong">{title}</div>
        <div className="text-[11px] text-muted leading-snug">{body}</div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  // Official Google "G" logo, inline so we don't need a network fetch.
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}
